import { StatusCounts, StatusAnalysis } from '../../utils/statusSummaryUtils';

/**
 * Determines the appropriate icon variant based on status counts
 * Used for summary items like MilestoneMap and MajorObjective
 */
export function getVariantFromStatusCounts(counts: StatusCounts): string | undefined {
  const hasGreen = counts.green > 0;
  const hasYellow = counts.yellow > 0;
  const hasRed = counts.red > 0;
  const hasPlanned = counts.planned > 0;
  const hasTbd = counts.tbd > 0;

  // Determine the primary status-based variant
  let statusVariant: string | undefined;

  if (hasGreen && hasYellow && hasRed) {
    statusVariant = 'summary-green-yellow-red';
  } else if (hasGreen && hasYellow) {
    statusVariant = 'summary-green-yellow';
  } else if (hasGreen && hasRed) {
    statusVariant = 'summary-green-red';
  } else if (hasYellow && hasRed) {
    statusVariant = 'summary-yellow-red';
  } else if (hasGreen) {
    statusVariant = 'summary-green';
  } else if (hasYellow) {
    statusVariant = 'summary-yellow';
  } else if (hasRed) {
    statusVariant = 'summary-red';
  }

  // If we have progress statuses, use them; otherwise use planning statuses
  if (statusVariant) {
    return statusVariant;
  }

  // Fallback to planning variants if no progress statuses
  if (hasPlanned && hasTbd) {
    return 'summary-planned-tbd';
  } else if (hasPlanned) {
    return 'summary-planned';
  } else if (hasTbd) {
    return 'summary-tbd';
  }

  return undefined;
}

/**
 * Determines the appropriate icon variant for individual status items
 * Used for individual Objective, Status, StatusReportDate items
 */
export function getVariantFromStatusAnalysis(analysis: StatusAnalysis): string | undefined {
  if (analysis.type === 'actual') {
    switch (analysis.statusCode) {
      case 'g':
      case 'G':
        return 'status-green';
      case 'y':
      case 'Y':
        return 'status-yellow';
      case 'r':
      case 'R':
        return 'status-red';
    }
  } else if (analysis.type === 'planning') {
    switch (analysis.statusCode) {
      case 'p':
        return 'status-planned';
      case '?':
        return 'status-tbd';
    }
  }

  return 'status-none';
}

/**
 * Determines the appropriate icon variant for results
 * Based on completion status and deadlines
 */
export function getVariantFromResultStatus(result: any): string | undefined {
  // Check if result is delivered/completed
  if (result.delivered === true) {
    return 'results-done';
  }

  // Check if deadline has passed and result is not delivered
  if (result.deadline && new Date(result.deadline) < new Date()) {
    return 'results-not-done';
  }

  // Default to pending for incomplete results with future or no deadlines
  return 'results-pending';
}

/**
 * Helper function to create variant metadata for DataNodes
 */
export function createVariantMetadata(variant: string | undefined): { variant?: string } {
  return variant ? { variant } : {};
}
